import { ArrowLeft, ArrowRight } from 'lucide-react';
import { TeamShirt, Typography } from '@dreamteam-monorepo/ui';
import { useTopTransfers } from '@/hooks/useTopTransfers';
import type { BETopTransferPlayer, TopTransferDirection } from '@/lib/network';

const TopTransferRow = ({ player }: { player: BETopTransferPlayer }) => {
  const isIncoming = player.direction === 'in';
  const club = player.clubShortName ?? player.clubName ?? 'Club TBC';
  const opponents =
    player.opponentShortNames.length > 0
      ? `vs ${player.opponentShortNames.join(', ')}`
      : 'Fixture TBC';
  const DirectionIcon = isIncoming ? ArrowLeft : ArrowRight;

  return (
    <li className="grid min-h-[88px] grid-cols-[28px_48px_minmax(0,1fr)_auto] items-center gap-[--padding-sm] border-b border-[--color-grey-200] px-[--padding-md] py-[--padding-sm] last:border-b-0">
      <Typography as="span" variant="subheadline030" className="text-[--color-neutral-900]">
        {player.rank}
      </Typography>

      <div className="relative h-12 w-12">
        <TeamShirt
          team={player.shirtKey ?? undefined}
          position={player.position}
          assetScale="1x"
          className="h-12 w-12"
        />
        <span
          aria-label={isIncoming ? 'Transferred in' : 'Transferred out'}
          className={`absolute -bottom-1 -right-1 flex size-5 items-center justify-center rounded-full text-white ${
            isIncoming ? 'bg-[#00A651]' : 'bg-[#E31B23]'
          }`}
        >
          <DirectionIcon aria-hidden="true" size={13} strokeWidth={3} />
        </span>
      </div>

      <div className="min-w-0">
        <Typography as="p" variant="subheadline020" className="truncate text-[--color-neutral-900]">
          {player.displayName}
        </Typography>
        <Typography as="p" variant="caption010" className="text-[--color-neutral-600]">
          {club} <span aria-hidden="true">|</span> {opponents}
        </Typography>
      </div>

      <Typography as="p" variant="subheadline020" className="text-right text-[--color-neutral-900]">
        £{player.currentValue.toFixed(1)}m
      </Typography>
    </li>
  );
};

const TopTransferList = ({
  title,
  direction,
  players,
}: {
  title: string;
  direction: TopTransferDirection;
  players: BETopTransferPlayer[];
}) => (
  <section
    className="overflow-hidden rounded-[--corner-md] border border-[--color-grey-200] bg-white"
    aria-labelledby={`top-transfers-${direction}`}
  >
    <div className="bg-[--page-header-bg-grey] px-[--padding-md] py-[--padding-sm]">
      <Typography
        id={`top-transfers-${direction}`}
        as="h3"
        variant="subheadline020"
        className="text-[--color-neutral-900]"
      >
        {title}
      </Typography>
    </div>

    {players.length > 0 ? (
      <ol>
        {players.map(player => (
          <TopTransferRow key={player.playerId} player={player} />
        ))}
      </ol>
    ) : (
      <div className="flex min-h-[160px] items-center justify-center px-[--padding-md] text-center">
        <Typography variant="paragraph020" className="text-[--color-neutral-600]">
          No applied transfers yet.
        </Typography>
      </div>
    )}
  </section>
);

const PanelState = ({ message }: { message: string }) => (
  <div className="flex min-h-[240px] items-center justify-center rounded-[--corner-md] border border-[--color-grey-200] bg-white px-[--padding-md] text-center">
    <Typography variant="paragraph020" className="text-[--color-neutral-600]">
      {message}
    </Typography>
  </div>
);

export const TopTransfersPanel = ({
  tournamentCalendarId,
}: {
  tournamentCalendarId: string | undefined;
}) => {
  const query = useTopTransfers(tournamentCalendarId);

  if (query.isPending) return <PanelState message="Loading top transfers…" />;
  if (query.isError)
    return <PanelState message="Failed to load top transfers. Please try again." />;
  if (!query.data?.gameweek) {
    return <PanelState message="Top transfers are not available until a Gameweek is open." />;
  }

  return (
    <div className="flex flex-col gap-[--padding-lg]">
      <Typography as="h2" variant="headline030" className="text-[--color-neutral-900]">
        GW{query.data.gameweek.number} Top Transfers
      </Typography>

      <div className="grid gap-[--padding-md] lg:grid-cols-2">
        <TopTransferList title="Transferred In" direction="in" players={query.data.transferredIn} />
        <TopTransferList
          title="Transferred Out"
          direction="out"
          players={query.data.transferredOut}
        />
      </div>
    </div>
  );
};
